import React from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../redux/reducers';
import { ErrorType } from '../../models/HttpError';
import { GetUserSetting, PutUserSetting } from '../../services/UserSettings.Services';
import { DashboardConfig } from './Dashboard.Types';
import { defaultDashboardConfig, normalizeDashboardConfig, readDashboardConfig, writeDashboardConfig } from './Dashboard.Logic';

/** Clave de la preferencia en Persons.API (`/persons/user-settings/{key}`). */
export const DASHBOARD_SETTING_KEY = 'dashboard.layout';
/** Espera entre el último cambio y el PUT al servidor (mover/redimensionar disparan muchos cambios seguidos). */
export const DASHBOARD_SAVE_DEBOUNCE_MS = 1000;

/**
 * - `loading`: pidiendo la configuración guardada en la cuenta.
 * - `saving` / `saved`: el último cambio viaja o ya está en el servidor.
 * - `local`: sin servidor (sin sesión o falló la llamada): los cambios quedan sólo en este navegador.
 * - `idle`: nada que informar.
 */
export type DashboardSyncStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'local';

export const serializeDashboardConfig = (config: DashboardConfig): string => JSON.stringify(config);

/** Deserializa lo que devolvió el servidor; `undefined` si no sirve. */
export const parseRemoteDashboardConfig = (value: string | null | undefined): DashboardConfig | undefined => {
  if (!value) return undefined;
  try {
    return normalizeDashboardConfig(JSON.parse(value));
  } catch {
    return undefined;
  }
};

const isNotFound = (error: unknown): boolean => (error as ErrorType | undefined)?.ErrorCode === 404;

const useAccessToken = (): string | undefined => useSelector((state: AppState) => state.oidc?.user?.access_token as string | undefined);

export interface DashboardSync {
  config: DashboardConfig;
  /** Aplica un cambio del usuario: lo guarda en este navegador y, con sesión, en la cuenta. */
  commit: (next: DashboardConfig | ((current: DashboardConfig) => DashboardConfig)) => void;
  status: DashboardSyncStatus;
}

/**
 * Configuración del panel con doble persistencia: `localStorage` como caché por usuario (lo que se ve
 * al instante y lo que queda si no hay servidor) y Persons.API como fuente de verdad para que viaje
 * entre equipos. Al entrar se lee la cuenta; si no hay nada guardado allá pero sí acá, se sube lo local.
 * Los cambios se suben con una pequeña espera; gana el último que escribe.
 */
export const useDashboardSync = (userId: string | undefined): DashboardSync => {
  const token = useAccessToken();
  const [config, setConfig] = React.useState<DashboardConfig>(() => readDashboardConfig(userId));
  const [status, setStatus] = React.useState<DashboardSyncStatus>('idle');
  /** Serialización de lo último que el servidor tiene (o recibió): evita PUTs sin cambios. */
  const syncedRef = React.useRef<string | undefined>(undefined);
  const pendingRef = React.useRef<DashboardConfig | undefined>(undefined);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const tokenRef = React.useRef(token);
  tokenRef.current = token;

  const flush = React.useCallback(async () => {
    timerRef.current = undefined;
    const next = pendingRef.current;
    pendingRef.current = undefined;
    const currentToken = tokenRef.current;
    if (!next || !currentToken) return;
    const serialized = serializeDashboardConfig(next);
    if (serialized === syncedRef.current) {
      setStatus((current) => (current === 'saving' ? 'saved' : current));
      return;
    }
    setStatus('saving');
    try {
      await PutUserSetting(currentToken, DASHBOARD_SETTING_KEY, serialized);
      syncedRef.current = serialized;
      setStatus('saved');
    } catch {
      setStatus('local');
    }
  }, []);

  const scheduleSave = React.useCallback(
    (next: DashboardConfig) => {
      if (!tokenRef.current) return;
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, DASHBOARD_SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  // Cada usuario tiene su panel: al cambiar el usuario logueado se lee su caché y luego su cuenta.
  React.useEffect(() => {
    let cancelled = false;
    const local = readDashboardConfig(userId);
    setConfig(local);
    syncedRef.current = undefined;
    if (!userId || !token) {
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');
    GetUserSetting(token, DASHBOARD_SETTING_KEY)
      .then((setting) => {
        if (cancelled) return;
        const remote = parseRemoteDashboardConfig(setting?.value);
        if (remote) {
          syncedRef.current = serializeDashboardConfig(remote);
          writeDashboardConfig(userId, remote);
          setConfig(remote);
          setStatus('saved');
          return;
        }
        // Había algo guardado pero no sirve: se reemplaza con lo local.
        scheduleSave(local);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isNotFound(error)) {
          // Primera vez en la cuenta: si en este navegador ya había un panel propio, se sube.
          if (serializeDashboardConfig(local) !== serializeDashboardConfig(defaultDashboardConfig())) scheduleSave(local);
          else setStatus('idle');
          return;
        }
        setStatus('local');
      });
    return () => {
      cancelled = true;
    };
  }, [userId, token, scheduleSave]);

  // Al desmontar, lo pendiente sale sin esperar la demora.
  React.useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        void flush();
      }
    },
    [flush]
  );

  const commit = React.useCallback(
    (next: DashboardConfig | ((current: DashboardConfig) => DashboardConfig)) => {
      setConfig((current) => {
        const resolved = typeof next === 'function' ? next(current) : next;
        if (resolved !== current) {
          writeDashboardConfig(userId, resolved);
          scheduleSave(resolved);
        }
        return resolved;
      });
    },
    [userId, scheduleSave]
  );

  return { config, commit, status };
};
